/**
 * ScannerSettings — Iter 311 Phase 2
 *
 * Admin-Toggles für die Tiefen-Sensor-Integration. Erlaubt:
 *   - Globalen Toggle (kein Depth wenn aus → reine Foto-Analyse)
 *   - Per-Plattform Toggle (iOS LiDAR / Android ARCore)
 *   - Mindest-Konfidenz-Schwelle (Server-Hint für Mobile)
 *
 * Geht NUR im "Settings → Erweitert" Hub.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, ScanEye, Info } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScannerSettings = () => {
  const [settings, setSettings] = useState({
    depth_capture_enabled: true,
    arcore_enabled: true,
    ios_lidar_enabled: true,
    depth_min_confidence: 0.6,
  });
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/scanner-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings({
        depth_capture_enabled: res.data?.depth_capture_enabled ?? true,
        arcore_enabled: res.data?.arcore_enabled ?? true,
        ios_lidar_enabled: res.data?.ios_lidar_enabled ?? true,
        depth_min_confidence: res.data?.depth_min_confidence ?? 0.6,
      });
    } catch (e) {
      // Defaults verwenden
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/scanner-settings`,
        {
          depth_capture_enabled: settings.depth_capture_enabled,
          arcore_enabled: settings.arcore_enabled,
          ios_lidar_enabled: settings.ios_lidar_enabled,
          depth_min_confidence: parseFloat(settings.depth_min_confidence),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Foto-Scanner Einstellungen gespeichert');
    } catch (e) {
      toast.error(`Fehler: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const masterOff = !settings.depth_capture_enabled;

  return (
    <Card data-testid="scanner-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScanEye className="h-5 w-5 text-purple-600" />
          Foto-Scanner Tiefen-Capture
        </CardTitle>
        <CardDescription>
          Steuert ob iPhone LiDAR + Android ARCore Tiefen-Daten an die KI
          weitergeben. Wenn deaktiviert: reine Foto-Analyse, gleiche Genauigkeit
          wie früher.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm font-semibold">Tiefen-Capture aktiv (global)</Label>
            <p className="text-xs text-gray-500 mt-1">
              Master-Toggle. Bei aus: alle LiDAR/ARCore-Daten werden serverseitig
              verworfen, KI macht reine Foto-Analyse.
            </p>
          </div>
          <Switch
            data-testid="depth-capture-master"
            checked={settings.depth_capture_enabled}
            onCheckedChange={(v) => setSettings(s => ({ ...s, depth_capture_enabled: v }))}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className={`flex items-center justify-between rounded-lg border p-3 ${masterOff ? 'opacity-50' : ''}`}>
            <div>
              <Label className="text-sm font-semibold">📱 iOS LiDAR</Label>
              <p className="text-xs text-gray-500 mt-0.5">iPhone 12 Pro+</p>
            </div>
            <Switch
              data-testid="ios-lidar-toggle"
              checked={settings.ios_lidar_enabled && !masterOff}
              disabled={masterOff}
              onCheckedChange={(v) => setSettings(s => ({ ...s, ios_lidar_enabled: v }))}
            />
          </div>
          <div className={`flex items-center justify-between rounded-lg border p-3 ${masterOff ? 'opacity-50' : ''}`}>
            <div>
              <Label className="text-sm font-semibold">🤖 Android ARCore</Label>
              <p className="text-xs text-gray-500 mt-0.5">Pixel / Samsung Flagships</p>
            </div>
            <Switch
              data-testid="arcore-toggle"
              checked={settings.arcore_enabled && !masterOff}
              disabled={masterOff}
              onCheckedChange={(v) => setSettings(s => ({ ...s, arcore_enabled: v }))}
            />
          </div>
        </div>

        <div className={`space-y-2 ${masterOff ? 'opacity-50' : ''}`}>
          <Label htmlFor="depth-conf" className="text-sm font-semibold">
            Mindest-Konfidenz für Capture
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="depth-conf"
              data-testid="depth-confidence-input"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={settings.depth_min_confidence}
              onChange={(e) => setSettings(s => ({
                ...s,
                depth_min_confidence: e.target.value,
              }))}
              className="w-32"
              disabled={masterOff}
            />
            <span className="text-xs text-gray-500">
              0.0 = jeder Frame akzeptiert · 1.0 = nur perfekte Stabilität
            </span>
          </div>
          <p className="text-xs text-gray-500">
            Default: 0.6 (gute Balance zwischen Genauigkeit und Capture-Zeit)
          </p>
        </div>

        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>Wann ausschalten?</strong>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Wenn auf Android-Test-Geräten die ARCore-Capture-Funktion Probleme macht (z.B. Threading-Issues), kannst du ARCore deaktivieren ohne iOS zu beeinflussen.</li>
                <li>Wenn du Vergleichs-Daten brauchst: 1 Woche ohne Tiefen-Capture vs 1 Woche mit, um den Genauigkeits-Unterschied zu messen.</li>
              </ul>
            </div>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-scanner-settings"
          className="w-full"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Speichern...' : 'Einstellungen speichern'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ScannerSettings;
